import React, { useState, useMemo } from 'react';
import { gql, useLazyQuery } from '@apollo/client';
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { DatePicker } from '@/components/ui/date-picker';
import { Button } from '@/components/ui/button';
import { SearchIcon } from 'lucide-react';
import { toast } from '@/components/ui/toast';
import { calculateUnits } from './calculateUnits';
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from '@/components/ui/table';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  LabelList,
} from 'recharts';

const PMPM_RATE = 408.2;

export const GET_CALAIM_NOTES = gql`
  query GetCalaimNotes($startDate: Date, $endDate: Date) {
    calaimNotes(startDate: $startDate, endDate: $endDate) {
      id
      participant
      firstName
      lastName
      procedurecode
      proceduremodifier
      procedurelength
      serviceStartDate
    }
  }
`;

function getProcedureKey(note: any): string {
  const code = note.procedurecode.trim().toUpperCase();
  const mod = note.proceduremodifier?.trim().toUpperCase() || '';
  return mod ? `${code} ${mod}` : code;
}

function getServiceDate(note: any): string {
  return note.serviceStartDate.split('T')[0];
}

function getMonthLabel(code: string) {
  const [year, month] = code.split('-').map(Number);
  return new Date(year, month - 1, 1).toLocaleString('default', { month: 'long' });
}

export const PMPM: React.FC = () => {
  const [startDate, setStartDate] = useState<Date | undefined>();
  const [endDate, setEndDate] = useState<Date | undefined>();
  const [fetchNotes, { data, error }] = useLazyQuery(GET_CALAIM_NOTES);

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedMonthCode, setSelectedMonthCode] = useState<string>('');
  const [selectedMonthName, setSelectedMonthName] = useState<string>('');

  const notes = useMemo(() => (data?.calaimNotes ?? []) as any[], [data]);

  const groupedUnits = useMemo(() => {
    const groupedMinutes: Record<string, number> = {};

    notes.forEach((note) => {
      const serviceDate = getServiceDate(note);
      const key = `${note.participant}|${serviceDate}|${getProcedureKey(note)}`;
      groupedMinutes[key] = (groupedMinutes[key] || 0) + Number(note.procedurelength || 0);
    });

    return Object.entries(groupedMinutes).reduce((units, [key, totalMinutes]) => {
      units[key] = calculateUnits(totalMinutes);
      return units;
    }, {} as Record<string, number>);
  }, [notes]);

  const monthlyData = useMemo(() => {
    const monthUnits: Record<string, number> = {};
    const monthMembers: Record<string, Set<string>> = {};

    Object.entries(groupedUnits).forEach(([key, units]) => {
      if (!units) {
        return;
      }

      const [participant, serviceDate] = key.split('|');
      const monthCode = serviceDate.slice(0, 7);
      monthUnits[monthCode] = (monthUnits[monthCode] || 0) + units;

      const members = monthMembers[monthCode] || new Set<string>();
      members.add(participant);
      monthMembers[monthCode] = members;
    });

    const monthCodes = Array.from(
      new Set(notes.map((note) => getServiceDate(note).slice(0, 7)))
    ).sort();

    const dataArray = monthCodes.map((code) => {
      const memberMonths = monthMembers[code]?.size || 0;
      return {
        code,
        month: getMonthLabel(code),
        units: monthUnits[code] || 0,
        memberMonths,
        amountPaid: memberMonths * PMPM_RATE,
      };
    });

    const totalUnits = dataArray.reduce((sum, item) => sum + item.units, 0);
    const totalMemberMonths = dataArray.reduce((sum, item) => sum + item.memberMonths, 0);
    const totalAmountPaid = dataArray.reduce((sum, item) => sum + item.amountPaid, 0);

    return {
      data: dataArray,
      totalUnits,
      totalMemberMonths,
      totalAmountPaid,
    };
  }, [groupedUnits, notes]);

  const rawDataForSelectedMonth = useMemo(
    () =>
      notes.filter(
        (note) => getServiceDate(note).slice(0, 7) === selectedMonthCode
      ),
    [notes, selectedMonthCode]
  );

  const handleSearch = () => {
    if (!startDate || !endDate) {
      toast.error('Please select both dates');
      return;
    }

    fetchNotes({ variables: { startDate, endDate } });
  };

  const handleDownloadAll = () => {
    if (!notes.length) {
      toast.error('No data to download');
      return;
    }

    const header = [
      'ID',
      'Participant',
      'First Name',
      'Last Name',
      'Code',
      'Modifier',
      'Minutes',
      'Date',
      'Units',
    ];

    const rows = notes.map((note) => {
      const serviceDate = getServiceDate(note);
      const key = `${note.participant}|${serviceDate}|${getProcedureKey(note)}`;
      return [
        note.id,
        note.participant,
        note.firstName,
        note.lastName,
        note.procedurecode,
        note.proceduremodifier,
        note.procedurelength,
        serviceDate,
        groupedUnits[key] || 0,
      ].map((value) => `"${value}"`).join(',');
    });

    const csv = [header.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'pmpm_raw_data.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4 text-sm">
      <div className="flex gap-4 items-end">
        <DatePicker date={startDate} setDate={setStartDate} label="Start Date" />
        <DatePicker date={endDate} setDate={setEndDate} label="End Date" />
        <Button onClick={handleSearch} className="flex items-center gap-2">
          <SearchIcon size={16} />
          <span>Search</span>
        </Button>
        <Button onClick={handleDownloadAll}>
          Download All Raw Data
        </Button>
      </div>

      {error && <div className="text-red-600">Error: {error.message}</div>}

      {data?.calaimNotes ? (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead></TableHead>
                {monthlyData.data.map((item) => (
                  <TableHead key={item.code}>
                    <button
                      className="text-blue-600 underline"
                      onClick={() => {
                        setSelectedMonthCode(item.code);
                        setSelectedMonthName(item.month);
                        setModalIsOpen(true);
                      }}
                    >
                      {item.month}
                    </button>
                  </TableHead>
                ))}
                <TableHead>Total</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell>Total Units</TableCell>
                {monthlyData.data.map((item) => (
                  <TableCell key={item.code}>{item.units}</TableCell>
                ))}
                <TableCell>{monthlyData.totalUnits}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Member Months</TableCell>
                {monthlyData.data.map((item) => (
                  <TableCell key={item.code}>{item.memberMonths}</TableCell>
                ))}
                <TableCell>{monthlyData.totalMemberMonths}</TableCell>
              </TableRow>
              <TableRow className="font-semibold">
                <TableCell>Amount Paid</TableCell>
                {monthlyData.data.map((item) => (
                  <TableCell key={item.code}>
                    {item.amountPaid.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    })}
                  </TableCell>
                ))}
                <TableCell>
                  {monthlyData.totalAmountPaid.toLocaleString('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  })}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>

          <div className="mt-4">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={[
                  ...monthlyData.data,
                  { month: 'Total', units: monthlyData.totalUnits },
                ]}
                margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="month"
                  label={{ value: 'Month', position: 'bottom', offset: 0 }}
                />
                <YAxis
                  allowDecimals={false}
                  label={{ value: 'Units', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip formatter={(value: number) => [value, 'Units']} />
                <Bar dataKey="units" fill="#8884d8">
                  <LabelList dataKey="units" position="top" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <Dialog
            open={modalIsOpen}
            onOpenChange={(open: boolean) => {
              if (!open) {
                setModalIsOpen(false);
              }
            }}
          >
            <DialogContent
              className="max-w-none overflow-auto"
              style={{ width: '80%', height: '80%', margin: 'auto' }}
              onInteractOutside={(event) => event.preventDefault()}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold">
                  Raw Data for {selectedMonthName}
                </h2>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID</TableHead>
                    <TableHead>Participant</TableHead>
                    <TableHead>First Name</TableHead>
                    <TableHead>Last Name</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Modifier</TableHead>
                    <TableHead>Minutes</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead>Units</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rawDataForSelectedMonth.map((note) => {
                    const serviceDate = getServiceDate(note);
                    const key = `${note.participant}|${serviceDate}|${getProcedureKey(note)}`;

                    return (
                      <TableRow key={note.id}>
                        <TableCell>{note.id}</TableCell>
                        <TableCell>{note.participant}</TableCell>
                        <TableCell>{note.firstName}</TableCell>
                        <TableCell>{note.lastName}</TableCell>
                        <TableCell>{note.procedurecode}</TableCell>
                        <TableCell>{note.proceduremodifier}</TableCell>
                        <TableCell>{note.procedurelength}</TableCell>
                        <TableCell>{serviceDate}</TableCell>
                        <TableCell>{groupedUnits[key] || 0}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </DialogContent>
          </Dialog>
        </>
      ) : (
        <div className="p-4 text-center text-muted-foreground">
          Set date range and click Search
        </div>
      )}
    </div>
  );
};
